"use client"

import styled from "@emotion/styled"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow } from "ui"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Static icons (currentColor = overlay text). Shown only when their line has text. ---

function PinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      <path
        d="M7.5 9.1665C7.5 9.82955 7.76339 10.4654 8.23223 10.9343C8.70107 11.4031 9.33696 11.6665 10 11.6665C10.663 11.6665 11.2989 11.4031 11.7678 10.9343C12.2366 10.4654 12.5 9.82955 12.5 9.1665C12.5 8.50346 12.2366 7.86758 11.7678 7.39874C11.2989 6.9299 10.663 6.6665 10 6.6665C9.33696 6.6665 8.70107 6.9299 8.23223 7.39874C7.76339 7.86758 7.5 8.50346 7.5 9.1665Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.7168 13.8806L11.1809 17.4164C10.8684 17.7286 10.4448 17.904 10.003 17.904C9.56129 17.904 9.13762 17.7286 8.82511 17.4164L5.28844 13.8806C4.35612 12.9482 3.72122 11.7603 3.46401 10.4671C3.20681 9.17394 3.33885 7.83352 3.84344 6.61536C4.34804 5.39721 5.20252 4.35604 6.29884 3.62351C7.39516 2.89098 8.68408 2.5 10.0026 2.5C11.3211 2.5 12.6101 2.89098 13.7064 3.62351C14.8027 4.35604 15.6572 5.39721 16.1618 6.61536C16.6664 7.83352 16.7984 9.17394 16.5412 10.4671C16.284 11.7603 15.6491 12.9482 14.7168 13.8806Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      {[
        "M2.5 17.5H17.5",
        "M7.5 6.6665H8.33333",
        "M7.5 10H8.33333",
        "M7.5 13.3335H8.33333",
        "M11.6641 6.66699H12.4974",
        "M11.6641 10H12.4974",
        "M11.6641 13.333H12.4974",
        "M4.16406 17.5V4.16667C4.16406 3.72464 4.33966 3.30072 4.65222 2.98816C4.96478 2.67559 5.3887 2.5 5.83073 2.5H14.1641C14.6061 2.5 15.03 2.67559 15.3426 2.98816C15.6551 3.30072 15.8307 3.72464 15.8307 4.16667V17.5",
      ].map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <rect x="4.5" y="3" width="4" height="14" rx="1" />
      <rect x="11.5" y="3" width="4" height="14" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M5 3.5v13l11-6.5L5 3.5z" />
    </svg>
  )
}

// --- Viewport entrance hook (fires once) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 60px 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
`

const Inner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 16px;
  }
`

/* title + subtitle + description are centered (Figma header align = CENTER) */
const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 48px;
  }
`

/* Rich-text title: a bold word renders underlined in the title colour (ASH-1516). */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 500;
  line-height: 1.1;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  /* emphasized word gets the hand-drawn brush underline from Figma (ASH-1797) */
  strong {
    position: relative;
    display: inline-block;
    font-weight: 700;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 16px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const Description = styled.p`
  max-width: 840px;
  margin: 24px auto 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 32px;
  }
`

const CarouselWrapper = styled.div`
  position: relative;

  .swiper {
    overflow: visible;
    padding: 0;
  }

  .swiper-wrapper {
    align-items: stretch;
  }

  /* fixed card width per Figma: 304 mobile, 441 desktop (slidesPerView="auto") */
  .swiper-slide {
    width: 304px;
    height: auto;
    display: flex;

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 441px;
    }
  }
`

const Card = styled.div<{ inView: boolean; index: number }>`
  position: relative;
  width: 100%;
  height: 308px;
  border-radius: 8px;
  overflow: hidden;
  color: #fff;
  text-decoration: none;
  display: block;

  opacity: ${({ inView }) => (inView ? 1 : 0)};
  /* slide in from the right into place on scroll-into-view (ASH-1798).
     translateX is applied via inline style so the emotion RTL plugin doesn't flip its sign. */
  transition: opacity 0.5s ease, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ index }) => index * 90}ms;

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const Media = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

/* dark gradient at the bottom so the white overlay text stays readable */
const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 35%, rgba(0, 0, 0, 0.85) 100%);
  pointer-events: none;
`

const Overlay = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  text-align: start;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 16px;
  }
`

const ProjectName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
`

const MetaLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.25;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`

const PlayPauseBtn = styled.button`
  /* inset:0 + margin:auto centers on both axes, RTL-safe (no left/transform to flip) */
  position: absolute;
  inset: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(4px);
  color: #fff;
  cursor: pointer;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 48px;
  }
`

const ArrowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--comp-text);

  svg {
    color: var(--comp-text);
    path {
      fill: var(--comp-text);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 100px;
  padding: 0;
  cursor: pointer;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Types & parsing ---

type ProjectCard = {
  mediaUrl: string
  mediaAlt: string
  projectName: string
  addressLine: string
  secondaryInfo: string
  link: { url?: string; name?: string; target?: string; type?: string } | null
}

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

function parseCard(block: any): ProjectCard {
  const media = extractProperty(block, "media", true) as any[] | undefined
  const link = extractProperty(block, "projectLink") as any[] | ""
  return {
    mediaUrl: media?.[0]?.url || "",
    mediaAlt: media?.[0]?.altText || "",
    projectName: (extractProperty(block, "projectName") as string) || "",
    addressLine: (extractProperty(block, "addressLine") as string) || "",
    secondaryInfo: (extractProperty(block, "secondaryInfo") as string) || "",
    link: (Array.isArray(link) && link[0]) || null,
  }
}

function isEmptyCard(card: ProjectCard): boolean {
  return !card.mediaUrl && !card.projectName
}

// --- Card ---

function ProjectCardView({
  card,
  inView,
  index,
  isRtl,
}: {
  card: ProjectCard
  inView: boolean
  index: number
  isRtl: boolean
}) {
  const analytics = useLandingAnalytics()
  const isVideo = isVideoUrl(card.mediaUrl)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    setIsPlaying(!isPlaying)
  }

  const href = card.link?.url ? getLinkHrefFromMultiUrlPickerItem(card.link as any) : undefined
  const linkProps: Record<string, string> = href
    ? {
        href,
        ...(card.link?.target ? { target: card.link.target } : {}),
        ...(card.link?.target === "_blank" ? { rel: "noopener noreferrer" } : {}),
      }
    : {}

  return (
    <Card
      as={href ? "a" : "div"}
      inView={inView}
      index={index}
      style={{ transform: inView ? "translateX(0)" : "translateX(80px)" }}
      onClick={() => href && analytics.projectClick(card.projectName)}
      {...linkProps}
    >
      {card.mediaUrl &&
        (isVideo ? (
          <MediaVideo
            ref={videoRef}
            src={getUmbracoMediaUrl(card.mediaUrl)}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <Media
            src={getUmbracoMediaUrl(card.mediaUrl)}
            fill
            alt={card.mediaAlt || card.projectName}
          />
        ))}
      <Gradient />
      {isVideo && (
        <PlayPauseBtn
          onClick={togglePlay}
          aria-label={
            isPlaying
              ? isRtl
                ? "עצור תנועת הרכיב"
                : "Stop animation"
              : isRtl
              ? "הפעל תנועה רכיב"
              : "Play animation"
          }
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </PlayPauseBtn>
      )}
      <Overlay>
        {card.projectName && <ProjectName>{card.projectName}</ProjectName>}
        {card.addressLine && (
          <MetaLine>
            <PinIcon />
            {card.addressLine}
          </MetaLine>
        )}
        {card.secondaryInfo && (
          <MetaLine>
            <BuildingIcon />
            {card.secondaryInfo}
          </MetaLine>
        )}
      </Overlay>
    </Card>
  )
}

// --- Main Component ---

function AdditionalProjects1({ data }: Props) {
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref: viewRef, inView } = useInViewOnce<HTMLDivElement>()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [snapCount, setSnapCount] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const syncSwiperState = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex)
    setSnapCount(swiper.snapGrid.length)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const title = extractProperty(data, "title") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const description = extractProperty(data, "description") as string | ""
  const cardBlocks = extractProperty(data, "projectCards") as any[] | ""
  const cards = (Array.isArray(cardBlocks) ? cardBlocks : [])
    .map(parseCard)
    .filter((card) => !isEmptyCard(card))

  if (!title && cards.length === 0) return null

  return (
    <Section>
      <Inner>
        <Header>
          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {description && <Description>{description}</Description>}
        </Header>
      </Inner>

      {/* full-bleed carousel: first card flush to the RTL start (right) edge, bleeds left (ASH-1800) */}
      {cards.length > 0 && (
        <CarouselWrapper ref={viewRef}>
          <Swiper
            onSwiper={(swiper) => {
              setSwiperRef(swiper)
              syncSwiperState(swiper)
            }}
            onSlideChange={syncSwiperState}
            onResize={syncSwiperState}
            dir={isRtl ? "rtl" : "ltr"}
            spaceBetween={24}
            slidesPerView="auto"
          >
            {cards.map((card, i) => (
              <SwiperSlide key={i} style={{ height: "auto" }}>
                <ProjectCardView card={card} inView={inView} index={i} isRtl={isRtl} />
              </SwiperSlide>
            ))}
          </Swiper>

          {snapCount > 1 && (
            <Controls>
              <ArrowBtn
                onClick={() => swiperRef?.slidePrev()}
                disabled={isBeginning}
                aria-label={isRtl ? "לפרויקטים הקודמים" : "Previous projects"}
              >
                {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
              </ArrowBtn>
              <Dots>
                {Array.from({ length: snapCount }).map((_, i) => (
                  <Dot
                    key={i}
                    active={i === activeIndex}
                    onClick={() => swiperRef?.slideTo(i)}
                    aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                  />
                ))}
              </Dots>
              <ArrowBtn
                onClick={() => swiperRef?.slideNext()}
                disabled={isEnd}
                aria-label={isRtl ? "לפרויקטים הבאים" : "Next projects"}
              >
                {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
              </ArrowBtn>
            </Controls>
          )}
        </CarouselWrapper>
      )}
    </Section>
  )
}

export default AdditionalProjects1
